import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, Drawer, Empty, Flex, Segmented, Switch, Tag, Typography } from 'antd';
import {
  ArrowClockwiseIcon,
  BellRingingIcon,
  BellSimpleIcon,
  CheckIcon,
  ClockCountdownIcon,
  SpeakerSimpleSlashIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { HubAlert } from '../hubAlertRules';
import { briefingTitle, type Briefing, type FacilityBacklog } from '../hubBriefingRules';
import { HubBriefingCard } from '../HubBriefingCard';
import type { AlertItem, AlertNotice, DrawerTab } from './alertCenterStore';
import { alertTitle, detailLine, keepFocus, relativeTime } from './alertFormat';

const RED = '#D92D20';

const DRAWER_CLASS = 'hub-alert-drawer';
/** On <body> while the bar shows: page messages (scan feedback) and notices move below it. */
const BAR_SHOWN_CLASS = 'hub-alert-bar-shown';
const BAR_SHOWN_STYLE = `
body.${BAR_SHOWN_CLASS} .ant-message,
body.${BAR_SHOWN_CLASS} .ant-notification-top,
body.${BAR_SHOWN_CLASS} .ant-notification-topLeft,
body.${BAR_SHOWN_CLASS} .ant-notification-topRight { top: 64px !important; }
`;
const AMBER = '#F79009';

export type SetupLine = { text: string; tone: 'ok' | 'warning' | 'error'; action: string };
/** Where alerts ring loudly on this screen, and the "Ring for all my facilities" switch. */
export type RingScope = { text: string; ringAll: boolean };

export type AlertCenterViewProps = {
  items: ReadonlyArray<AlertItem>;
  waiting: Briefing | null;
  waitingUnseen: boolean;
  drawer: { open: boolean; tab: DrawerTab; fresh: ReadonlyArray<string>; auto?: boolean };
  isMobile: boolean;
  /** Page sound not armed yet (no tap since Hub opened in a tab). */
  soundLocked: boolean;
  /** Names the facility on alerts (several facilities, or All Facilities on screen). */
  showFacility: boolean;
  isElsewhere: (alert: HubAlert) => boolean;
  canSwitchTo: (facilityId: number | null) => boolean;
  canOpenPackages: boolean;
  setup: SetupLine | null;
  /** A calm notice (alerts that did not ring here), with its still-unread alerts, newest first. */
  notice?: { kind: AlertNotice['kind']; items: ReadonlyArray<AlertItem> } | null;
  /** Operators with several facilities: where alerts ring loudly. */
  ringScope?: RingScope | null;
  now?: number;
  onView: (tab?: DrawerTab) => void;
  onAcknowledgeAll: () => void;
  onOpen: (item: AlertItem) => void;
  onStart: (backlog: FacilityBacklog, url: string) => void;
  /** Reloads what is waiting; rejects when it could not load. */
  onRefreshWaiting?: () => Promise<void>;
  /** Hides the calm waiting bar without opening the list (the bell still has it). */
  onDismissWaiting?: () => void;
  onTab: (tab: DrawerTab) => void;
  onClose: () => void;
  onSetup: () => void;
  onDismissNotice?: () => void;
  onRingAll?: (ringAll: boolean) => void;
};

const facilityLabel = (alert: HubAlert, props: AlertCenterViewProps) =>
  (props.showFacility || props.isElsewhere(alert)) && alert.facilityName
    ? alert.facilityName
    : null;

const CALM_BAR: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #D0D5DD',
  borderLeft: '4px solid #393AFB',
  boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
};
const ONE_LINE: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

/**
 * Alerts that did not ring here (another facility than the one selected): a calm bar for a few
 * seconds that names the facility and opens the alert (switching to its facility), never ringing
 * and never taking focus. `here`: unread alerts of the facility just switched to.
 */
const NoticeBar = ({
  notice,
  bar,
  ...props
}: AlertCenterViewProps & {
  notice: NonNullable<AlertCenterViewProps['notice']>;
  bar: React.CSSProperties;
}) => {
  const newest = notice.items[0];
  const { alert } = newest;
  const count = notice.items.length;
  const facility = alert.facilityName;
  const here = notice.kind === 'here';
  const switching = props.isElsewhere(alert) && props.canSwitchTo(alert.facilityId);
  const title = here
    ? `${count} new ${count === 1 ? 'alert' : 'alerts'} here`
    : alertTitle(alert, Boolean(facility));
  // A phone has no room for the tag: the facility goes on a second line (it is the point here).
  const stacked = props.isMobile && !here && Boolean(facility);
  return (
    <div
      role="status"
      data-hub-alert-ui
      style={{ ...bar, ...CALM_BAR }}
    >
      <BellSimpleIcon size={20} weight="duotone" color="#393AFB" style={{ flexShrink: 0 }} />
      {stacked ? (
        <Flex vertical style={{ minWidth: 0, flex: 1, lineHeight: 1.25 }}>
          <Typography.Text strong style={ONE_LINE}>
            {title}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ ...ONE_LINE, fontSize: 12 }}>
            {facility}
          </Typography.Text>
        </Flex>
      ) : (
        <Typography.Text strong style={{ ...ONE_LINE, flexShrink: 1 }}>
          {title}
        </Typography.Text>
      )}
      {facility && !stacked && (
        <Tag style={{ margin: 0, flexShrink: 0 }}>{facility}</Tag>
      )}
      {!here && !props.isMobile && (
        <Typography.Text type="secondary" style={{ ...ONE_LINE, flex: 1 }}>
          {detailLine(alert)}
        </Typography.Text>
      )}
      {(here || props.isMobile) && !stacked && <span style={{ flex: 1 }} />}
      {!here && count > 1 && (
        <Typography.Text strong style={{ flexShrink: 0 }}>
          +{count - 1}
        </Typography.Text>
      )}
      {here || count > 1 ? (
        <Button
          size="middle"
          type="primary"
          onMouseDown={keepFocus}
          onClick={() => props.onView('alerts')}
          style={{ flexShrink: 0 }}
        >
          View
        </Button>
      ) : (
        <Button
          size="middle"
          type="primary"
          onMouseDown={keepFocus}
          onClick={() => props.onOpen(newest)}
          style={{ flexShrink: 0 }}
        >
          {switching ? (props.isMobile ? 'Switch' : 'Switch and open') : 'Open'}
        </Button>
      )}
      {props.onDismissNotice && (
        <Button
          size="middle"
          type="text"
          aria-label="Dismiss"
          icon={<XIcon size={16} />}
          onMouseDown={keepFocus}
          onClick={props.onDismissNotice}
          style={{ flexShrink: 0 }}
        />
      )}
    </div>
  );
};

/**
 * The one transient surface: while an alert rings, a slim bar over Hub's own top bar with the
 * newest alert, View and Acknowledge. Task windows open below that band (and keep their actions
 * at the bottom), so it never covers the scan box or the task's buttons, and it never takes focus
 * (barcode scanners keep typing into the page). Without ringing alerts, calm versions announce
 * alerts that did not ring here (NoticeBar), or a new waiting list when the drawer could not open
 * by itself (work in progress).
 */
const AlertPill = (props: AlertCenterViewProps) => {
  const ringing = props.items.filter(item => item.state === 'ringing');
  const noticed = ringing.length === 0 && props.notice?.items.length ? props.notice : null;
  const calm =
    ringing.length === 0 && !noticed && props.waitingUnseen && props.waiting && !props.drawer.open;
  const shown = !props.drawer.open && (ringing.length > 0 || Boolean(noticed) || Boolean(calm));
  useEffect(() => {
    if (!shown) return undefined;
    document.body.classList.add(BAR_SHOWN_CLASS);
    return () => document.body.classList.remove(BAR_SHOWN_CLASS);
  }, [shown]);
  if (!shown) return null;

  const bar: React.CSSProperties = {
    position: 'fixed',
    zIndex: 2100,
    top: props.isMobile ? 'calc(4px + env(safe-area-inset-top))' : 8,
    ...(props.isMobile
      ? { left: 8, right: 8 }
      : { left: '50%', transform: 'translateX(-50%)', width: 'min(820px, calc(100vw - 32px))' }),
    minHeight: 48,
    borderRadius: 12,
    padding: '6px 8px 6px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };
  if (noticed) {
    return createPortal(<NoticeBar {...props} notice={noticed} bar={bar} />, document.body);
  }

  if (calm && props.waiting) {
    return createPortal(
      <div
        role="status"
        data-hub-alert-ui
        style={{ ...bar, ...CALM_BAR }}
      >
        <ClockCountdownIcon size={20} weight="duotone" color="#393AFB" style={{ flexShrink: 0 }} />
        <Typography.Text strong style={{ ...ONE_LINE, flex: 1 }}>
          {briefingTitle(props.waiting)}
        </Typography.Text>
        <Button
          size="middle"
          type="primary"
          onMouseDown={keepFocus}
          onClick={() => props.onView('waiting')}
        >
          View
        </Button>
        {props.onDismissWaiting && (
          <Button
            size="middle"
            type="text"
            aria-label="Dismiss"
            icon={<XIcon size={16} />}
            onMouseDown={keepFocus}
            onClick={props.onDismissWaiting}
            style={{ flexShrink: 0 }}
          />
        )}
      </div>,
      document.body,
    );
  }

  const newest = ringing[0].alert;
  const facility = facilityLabel(newest, props);
  const more = ringing.length - 1;
  return createPortal(
    <div
      role="alert"
      data-hub-alert-ui
      aria-label={`${ringing.length} ringing ${ringing.length === 1 ? 'alert' : 'alerts'}`}
      style={{
        ...bar,
        background: RED,
        color: '#fff',
        boxShadow: '0 12px 32px rgba(16,24,40,0.35)',
      }}
    >
      <BellRingingIcon size={20} weight="fill" aria-hidden style={{ flexShrink: 0 }} />
      <Typography.Text strong style={{ ...ONE_LINE, color: '#fff', flexShrink: 1 }}>
        {/* On a phone the facility tag is hidden: the title keeps the facility. */}
        {alertTitle(newest, Boolean(facility) && !props.isMobile)}
      </Typography.Text>
      {facility && !props.isMobile && (
        <Tag color="#fff" style={{ color: RED, fontWeight: 600, margin: 0, flexShrink: 0 }}>
          {facility}
        </Tag>
      )}
      {newest.priority === 'URGENT' && !props.isMobile && (
        <Tag
          color="#FDB022"
          style={{ color: '#1D2939', fontWeight: 700, margin: 0, flexShrink: 0 }}
        >
          URGENT
        </Tag>
      )}
      {!props.isMobile && (
        <Typography.Text style={{ ...ONE_LINE, color: '#fff', opacity: 0.9, flex: 1 }}>
          {detailLine(newest)}
        </Typography.Text>
      )}
      {props.isMobile && <span style={{ flex: 1 }} />}
      {props.soundLocked && !props.isMobile && (
        <SpeakerSimpleSlashIcon
          size={18}
          aria-label="Sound is off until you tap Hub once"
          style={{ flexShrink: 0 }}
        />
      )}
      {more > 0 && (
        <Typography.Text strong style={{ color: '#fff', flexShrink: 0 }}>
          +{more}
        </Typography.Text>
      )}
      <Button
        size="middle"
        style={{ flexShrink: 0 }}
        aria-label={more > 0 ? `View all ${ringing.length} alerts` : 'View alert'}
        onMouseDown={keepFocus}
        onClick={() => props.onView('alerts')}
      >
        View
      </Button>
      <Button
        size="middle"
        onMouseDown={keepFocus}
        onClick={props.onAcknowledgeAll}
        aria-label="Acknowledge"
        icon={props.isMobile ? <CheckIcon size={18} weight="bold" /> : undefined}
        style={{ flexShrink: 0, background: 'transparent', color: '#fff', borderColor: '#fff' }}
      >
        {props.isMobile ? null : 'Acknowledge'}
      </Button>
    </div>,
    document.body,
  );
};

const AlertRow = ({
  item,
  fresh,
  props,
}: {
  item: AlertItem;
  fresh: boolean;
  props: AlertCenterViewProps;
}) => {
  const { alert } = item;
  const facility = facilityLabel(alert, props);
  const urgent = alert.priority === 'URGENT';
  const switching = props.isElsewhere(alert) && props.canSwitchTo(alert.facilityId);
  return (
    <Flex
      gap={10}
      align="flex-start"
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        background: fresh ? '#FEF3F2' : '#F9FAFB',
        borderLeft: `4px solid ${urgent ? AMBER : fresh ? RED : '#D0D5DD'}`,
      }}
    >
      <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Flex align="center" gap={6} wrap>
          <Typography.Text strong={fresh}>{alertTitle(alert, Boolean(facility))}</Typography.Text>
          {facility && <Tag style={{ margin: 0 }}>{facility}</Tag>}
          {urgent && (
            <Tag color="gold" style={{ margin: 0, fontWeight: 700 }}>
              URGENT
            </Tag>
          )}
          {item.state === 'ringing' && (
            <Tag color="red" style={{ margin: 0, fontWeight: 700 }}>
              Ringing
            </Tag>
          )}
        </Flex>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {detailLine(alert)}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {relativeTime(item.at, props.now)}
          {item.state === 'opened' ? ' · Opened' : ''}
        </Typography.Text>
      </Flex>
      <Button
        type={fresh ? 'primary' : 'default'}
        size="small"
        onClick={() => props.onOpen(item)}
        style={{ flexShrink: 0 }}
      >
        {switching ? 'Switch and open' : 'Open'}
      </Button>
    </Flex>
  );
};

const AlertsTab = (props: AlertCenterViewProps) => {
  if (props.items.length === 0) {
    return <Empty description="No order alerts today" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  const fresh = props.items.filter(item => props.drawer.fresh.includes(item.alert.key));
  const earlier = props.items.filter(item => !props.drawer.fresh.includes(item.alert.key));
  return (
    <Flex vertical gap={16}>
      {fresh.length > 0 && (
        <Flex vertical gap={8}>
          <Typography.Text type="secondary" strong style={{ fontSize: 12, letterSpacing: 0.4 }}>
            NEW
          </Typography.Text>
          {fresh.map(item => (
            <AlertRow key={item.alert.key} item={item} fresh props={props} />
          ))}
        </Flex>
      )}
      {earlier.length > 0 && (
        <Flex vertical gap={8}>
          <Typography.Text type="secondary" strong style={{ fontSize: 12, letterSpacing: 0.4 }}>
            EARLIER TODAY
          </Typography.Text>
          {earlier.map(item => (
            <AlertRow key={item.alert.key} item={item} fresh={false} props={props} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

/** A waiting list older than this reloads when the Waiting tab is shown. */
const WAITING_STALE_MS = 5 * 60_000;

const WaitingTab = (props: AlertCenterViewProps) => {
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = () => {
    if (!props.onRefreshWaiting) return;
    setRefreshing(true);
    setFailed(false);
    props
      .onRefreshWaiting()
      .catch(() => setFailed(true))
      .finally(() => setRefreshing(false));
  };
  // Hub keeps the list across reloads instead of reloading it on every page load: refresh it when
  // looked at and no longer recent.
  const stale = !props.waiting || (props.now ?? Date.now()) - props.waiting.at > WAITING_STALE_MS;
  const shown = props.drawer.open;
  useEffect(() => {
    if (shown && stale) refresh();
    // Only when the tab is shown, not on every render.
  }, [shown]);
  return (
    <Flex vertical gap={12}>
      <Flex align="center" justify="space-between">
        <Typography.Text type={failed ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
          {failed
            ? 'Could not refresh. Check the connection and try again.'
            : props.waiting
              ? `Updated ${new Date(props.waiting.at).toLocaleTimeString('en-IN', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}`
              : 'Nothing loaded yet'}
        </Typography.Text>
        {props.onRefreshWaiting && (
          <Button
            size="small"
            type="text"
            loading={refreshing}
            icon={<ArrowClockwiseIcon size={14} />}
            onClick={refresh}
          >
            Refresh
          </Button>
        )}
      </Flex>
      {props.waiting && props.waiting.facilities.length > 0 ? (
        <>
          <Typography.Text strong>{briefingTitle(props.waiting)}</Typography.Text>
          <HubBriefingCard
            briefing={props.waiting}
            canOpenPackages={props.canOpenPackages}
            onStart={props.onStart}
            maxRows={20}
          />
        </>
      ) : (
        <Empty description="Nothing waiting" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Flex>
  );
};

const SETUP_COLORS: Record<SetupLine['tone'], string> = {
  ok: '#079455',
  warning: '#DC6803',
  error: RED,
};

/** Everything about order alerts in one place: the day's alerts, what is waiting, this device. */
const AlertDrawer = (props: AlertCenterViewProps) => {
  const waitingCount = props.waiting?.facilities.length ?? 0;
  const ringingCount = props.items.filter(item => item.state === 'ringing').length;
  // An alert that arrives while the Waiting tab is shown: a dot on the Alerts tab points to it.
  const unread = props.items.some(item => item.state === 'ringing' || item.state === 'new');
  // Closing the drawer puts focus back on what the operator last worked in (usually a scan box), so
  // the next barcode scan lands in the task, not on the page. Remembered continuously: by the time
  // the drawer opens, focus has already moved into it.
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const remember = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        !target.closest(`.${DRAWER_CLASS}, [data-hub-alert-ui]`)
      ) {
        returnFocus.current = target;
      }
    };
    document.addEventListener('focusin', remember);
    return () => document.removeEventListener('focusin', remember);
  }, []);
  return (
    <Drawer
      open={props.drawer.open}
      onClose={props.onClose}
      rootClassName={DRAWER_CLASS}
      // Opened by itself (a waiting list): focus stays in the scan box.
      autoFocus={!props.drawer.auto}
      afterOpenChange={open => {
        const target = returnFocus.current;
        if (!open && target?.isConnected) {
          // After the drawer's own focus handling has finished.
          window.setTimeout(() => target.focus({ preventScroll: true }), 0);
        }
      }}
      title="Order alerts"
      // An alert that arrives while the drawer is open rings here too: acknowledge without closing.
      extra={
        ringingCount > 0 ? (
          <Button danger type="primary" size="small" onClick={props.onAcknowledgeAll}>
            Acknowledge
          </Button>
        ) : undefined
      }
      placement={props.isMobile ? 'bottom' : 'right'}
      width={props.isMobile ? undefined : 420}
      height={props.isMobile ? '82dvh' : undefined}
      // Above an open task modal (picking, QC): looking at alerts must not need closing the task.
      zIndex={1100}
      styles={{ body: { padding: 16 } }}
      footer={
        <Flex vertical gap={6}>
          {props.soundLocked && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Tap anywhere on Hub once to turn on the alarm sound.
            </Typography.Text>
          )}
          {props.ringScope && (
            <Flex vertical gap={4}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {props.ringScope.text}
              </Typography.Text>
              {props.onRingAll && (
                <Flex align="center" justify="space-between" gap={8}>
                  <Typography.Text style={{ fontSize: 13 }}>
                    Ring for all my facilities
                  </Typography.Text>
                  <Switch
                    size="small"
                    aria-label="Ring for all my facilities"
                    checked={props.ringScope.ringAll}
                    onChange={props.onRingAll}
                  />
                </Flex>
              )}
            </Flex>
          )}
          {props.setup && (
            <Flex align="center" justify="space-between" gap={8}>
              <Typography.Text style={{ color: SETUP_COLORS[props.setup.tone], fontSize: 13 }}>
                {props.setup.text}
              </Typography.Text>
              <Button size="small" type="link" onClick={props.onSetup} style={{ paddingInline: 0 }}>
                {props.setup.action}
              </Button>
            </Flex>
          )}
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <Segmented
          block
          value={props.drawer.tab}
          onChange={value => props.onTab(value as DrawerTab)}
          options={[
            {
              label: (
                <Badge dot={unread && props.drawer.tab !== 'alerts'} offset={[6, 0]}>
                  {`Alerts${props.items.length ? ` (${props.items.length})` : ''}`}
                </Badge>
              ),
              value: 'alerts',
            },
            {
              label: `Waiting${waitingCount ? ` (${waitingCount})` : ''}`,
              value: 'waiting',
            },
          ]}
        />
        {props.drawer.tab === 'alerts' ? <AlertsTab {...props} /> : <WaitingTab {...props} />}
      </Flex>
    </Drawer>
  );
};

/** Presentational alert center (pill + drawer); see AlertCenter.tsx for the wiring. */
export const AlertCenterView = (props: AlertCenterViewProps) => (
  <>
    <style>{BAR_SHOWN_STYLE}</style>
    <AlertPill {...props} />
    <AlertDrawer {...props} />
  </>
);
